import {
  Bar,
  ChartSkeleton,
  ChatPanelSkeleton,
  FeedSkeleton,
} from "../CoTrader/CoTraderSkeletons";
import "../CoTrader/CoTrader.css";
import "./ProductDashboard.css";

// Loading placeholders for the desktop Product Dashboard. They reuse the real
// layout classes (CoTrader desk + pd-*) so nothing shifts when data lands.

const InfoFlowSkeleton = () => (
  <section className="ct-panel ct-insights" aria-hidden="true">
    <div className="ct-tabs">
      <div className="ct-tab">
        <Bar width={72} height={14} />
      </div>
    </div>
    <div className="ct-feed">
      <FeedSkeleton count={3} />
    </div>
  </section>
);

const EVEN = [50, 50];
const DEFAULT_WORKSPACE = {
  shellLayout: "stacked",
  chartPosition: "top",
  emgptPosition: "right",
};

// Flex split whose children take the saved panel sizes (in DOM order), so a
// skeleton keeps the proportions the user dragged the real panes to.
const Split = ({ direction, sizes, children }) => (
  <div
    style={{ display: "flex", flexDirection: direction, gap: 16, flex: "1 1 0", minWidth: 0, minHeight: 0 }}
  >
    {children.map((child, index) => (
      <div
        key={index}
        style={{ flex: `${sizes[index] ?? 50} 1 0`, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {child}
      </div>
    ))}
  </div>
);

// Pairs two panes in the order the real layout renders them.
const orderPair = (first, second, secondFirst) =>
  secondFirst ? [second, first] : [first, second];

// Loading desk that follows the user's workspace (shell layout, pane
// positions, collapsed panes) and last-dragged sizes. `layout` carries the
// saved sizes per panel group: { row, stack, shell } (each optional).
export const ProductDeskSkeleton = ({ workspace = DEFAULT_WORKSPACE, layout = {} }) => {
  const { shellLayout, chartPosition, emgptPosition } = { ...DEFAULT_WORKSPACE, ...workspace };
  const showChart = chartPosition !== "collapsed";
  const showAssistant = emgptPosition !== "collapsed";
  const chartBottom = chartPosition === "bottom";
  const assistantLeft = emgptPosition === "left";
  const infoFlow = <InfoFlowSkeleton />;
  const assistant = <ChatPanelSkeleton />;
  const chart = <ChartSkeleton />;

  // Info Flow beside the assistant, or alone when the assistant is hidden.
  const row = showAssistant ? (
    <Split direction="row" sizes={layout.row || EVEN}>
      {orderPair(infoFlow, assistant, assistantLeft)}
    </Split>
  ) : (
    infoFlow
  );

  let body;
  if (shellLayout === "assistantColumn") {
    // Chart over Info Flow in a column, assistant spanning the full height.
    const column = showChart ? (
      <Split direction="column" sizes={layout.stack || EVEN}>
        {orderPair(chart, infoFlow, chartBottom)}
      </Split>
    ) : (
      infoFlow
    );
    body = showAssistant ? (
      <Split direction="row" sizes={layout.shell || EVEN}>
        {orderPair(column, assistant, assistantLeft)}
      </Split>
    ) : (
      column
    );
  } else {
    body = showChart ? (
      <Split direction="column" sizes={layout.stack || EVEN}>
        {orderPair(chart, row, chartBottom)}
      </Split>
    ) : (
      row
    );
  }

  return (
    <div className="ct-desk-skeleton" role="status" aria-label="Loading dashboard">
      {body}
    </div>
  );
};

export const ProductChromeSkeleton = () => (
  <>
    <div className="px-3 pt-3 pb-2" aria-hidden="true">
      <div className="ct-chrome pd-chrome pd-chrome-head px-3 py-2">
        <Bar width={240} height={16} />
        <div className="d-flex gap-2">
          <Bar width={112} height={30} radius={999} />
          <Bar width={30} height={30} radius={10} />
        </div>
      </div>
    </div>
    <div className="px-3 pb-2" aria-hidden="true">
      <div className="ct-chrome pd-chrome pd-chrome-skeleton-filters px-3 py-2">
        {[260, 180, 220, 150].map((width) => (
          <div key={width} className="d-flex flex-column gap-2">
            <Bar width={64} height={8} />
            <Bar width={width} height={42} radius={15} />
          </div>
        ))}
      </div>
    </div>
  </>
);

// Full page (chrome + desk). Used by the global refresh skeleton.
export const ProductDashboardPageSkeleton = () => (
  <div className="ct-page pd-page w-100 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
    <ProductChromeSkeleton />
    <ProductDeskSkeleton />
  </div>
);
